import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  query,
  getOrder,
  changeCurrentPage,
  setOrder,
  changeMode,
  changeShowMyOrder as changeShowMyOrderAction,
} from '../../Actions/Order.action';
import OrderHistoryList from './OrderHistoryList';
import OrderHistoryB2BDetail from './OrderHistoryB2BDetail';
import OrderHistoryB2CDetail from './OrderHistoryB2CDetail';
import { calculatePager } from '../../Services/Pagination.service';
import { translate } from '../../Services/translation';
import { PaginationOptions, ViewMode } from '../../constants';
import { isEmpty } from 'lodash-es';
import { cn } from '../../Helpers/cn';
import Pagination from '../Pagination';

import { parseAsBoolean, parseAsInteger, useQueryState } from 'nuqs';
import LoaderOverlay from '../primitives/LoaderOverlay';

const OrderListContainer = () => {
  const {
    mode,
    totalCount,
    currentPage,
    showOnlyMyOrders,
    errors,
    requestStatus,
    list,
    availableStatusFilters,
  } = useSelector((state) => state.myPage.orders);
  const {
    isBusinessCustomer,
    hasApproverRole,
    tabsConstants,
    resellerStatuses,
    orderPageUrl,
    resellerOrderPageUrl,
  } = useSelector((state) => state.myPage);
  const [pager, setPager] = useState({});
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useQueryState('currentTab');
  const [orderId, setOrderId] = useQueryState('id');
  const [pageIndex, setPageIndex] = useQueryState(
    'page',
    parseAsInteger.withDefault(currentPage || 1)
  );
  const [currentShowMyOrder, setCurrentShowMyOrder] = useQueryState(
    'showMyOrders',
    parseAsBoolean.withDefault(showOnlyMyOrders || false)
  );
  const [searchQuery, setSearchQuery] = useQueryState('searchQuery');
  const [statusFilter, setStatusFilter] = useQueryState('statusFilter');

  const isReseller = currentTab === tabsConstants.customerOrderTab;
  const isOrderDetail = currentTab === tabsConstants.orderDetail;
  const isResellerDetail = currentTab === tabsConstants.customerOrderDetail;

  const statuses = availableStatusFilters || resellerStatuses;

  /** @type {string} */
  const orderListingTab =
    isReseller || isResellerDetail
      ? tabsConstants.customerOrderTab
      : tabsConstants.orderHistory;
  /** @type {string} */
  const orderDetailTab =
    isReseller || isResellerDetail
      ? tabsConstants.customerOrderDetail
      : tabsConstants.orderDetail;
  /** @type {string} */
  const printPageBaseUrl = isResellerDetail
    ? resellerOrderPageUrl
    : orderPageUrl;

  // Order listing fetch
  useEffect(() => {
    if (!isOrderDetail && !isResellerDetail) {
      const debounceTimer = setTimeout(() => {
        dispatch(
          query(
            {
              pageIndex: pageIndex,
              showOnlyMyOrders: currentShowMyOrder,
              searchQuery,
              statusFilter,
            },
            { isReseller: isReseller || isResellerDetail }
          )
        );
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [dispatch, pageIndex, currentShowMyOrder, searchQuery, statusFilter]);

  // Order fetching when going back to listing from detail if list is empty (ie. first landed on detail page)
  useEffect(() => {
    if (
      !isOrderDetail &&
      !isResellerDetail &&
      !list &&
      requestStatus !== 'idle'
    ) {
      const debounceTimer = setTimeout(() => {
        dispatch(
          query(
            {
              pageIndex: pageIndex,
              showOnlyMyOrders: currentShowMyOrder,
              searchQuery,
              statusFilter,
            },
            { isReseller: isReseller || isResellerDetail }
          )
        );
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [currentTab]);

  // Order detail fetch
  useEffect(() => {
    if ((isOrderDetail || isResellerDetail) && orderId) {
      const debounceTimer = setTimeout(() => {
        dispatch(getOrder(orderId, isReseller || isResellerDetail));
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [dispatch, currentTab, orderId]);

  useEffect(() => {
    setPager(calculatePager(totalCount, pageIndex));
  }, [totalCount, pageIndex]);

  const showDetail = useCallback(
    (order) => {
      setCurrentTab(orderDetailTab);
      setOrderId(isReseller ? order.id : order.orderId);
    },
    [dispatch]
  );

  const showList = useCallback(() => {
    dispatch(setOrder({}));
    dispatch(changeMode(ViewMode.List));
    setCurrentTab(orderListingTab);
    setOrderId(null);
  }, [dispatch]);

  const changeShowMyOrder = useCallback((checked) => {
    dispatch(changeShowMyOrderAction(checked));
    setCurrentShowMyOrder(checked);
    dispatch(changeCurrentPage(1));
    setPageIndex(1);
  }, []);

  const changePageIndex = useCallback(
    (index) => {
      if (index !== pageIndex) {
        dispatch(changeCurrentPage(index));
        setPageIndex(index);
      }
    },
    [pageIndex, dispatch]
  );

  const onApproveOrderCallback = useCallback(
    (orderId, showOrderDetail = false) => {
      dispatch(
        query(
          {
            pageIndex: pageIndex,
            showOnlyMyOrders: currentShowMyOrder,
            pageSize: PaginationOptions.PageSize,
          },
          {
            orderId,
            showOrderDetail,
            isReseller,
          }
        )
      );
    },
    [pageIndex, currentShowMyOrder, dispatch]
  );

  const isLoading = requestStatus === 'idle';

  return (
    <Fragment>
      {mode && mode !== ViewMode.List && (
        <Fragment>
          {isBusinessCustomer ? (
            <OrderHistoryB2BDetail
              hasApproverRole={hasApproverRole}
              onApproveOrderCallback={onApproveOrderCallback}
              onDismiss={showList}
              isReseller={isResellerDetail || isReseller}
              printPageBaseUrl={printPageBaseUrl}
            />
          ) : (
            <OrderHistoryB2CDetail
                onDismiss={showList}
                isReseller={isResellerDetail || isReseller}
                printPageBaseUrl={printPageBaseUrl}
            />
          )}
        </Fragment>
      )}
      {errors && !isEmpty(errors) && (
        <div className="page-content mb-2 space-y-1">
          {Object.keys(errors).map((key, i) => (
            <div key={i} className="form__validator--error">
              {translate(errors[key])}
            </div>
          ))}
        </div>
      )}
      {!mode && requestStatus === 'idle' && (
        <div className="page-content relative min-h-[887px] overflow-clip">
          <LoaderOverlay />
        </div>
      )}
      {requestStatus === 'error' && (
        <div className="page-content min-h-[200px] overflow-clip flex items-center justify-center mb-2">
          {translate('orderlist.error')}
        </div>
      )}
      {mode && mode === ViewMode.List && (
        <div
          className={cn('page-content relative', isLoading && 'overflow-clip')}
        >
          {!isReseller &&
            !isResellerDetail &&
            isBusinessCustomer &&
            hasApproverRole && (
              <div className="flex items-center gap-2 mb-2">
                <input
                  className="size-4 cursor-pointer"
                  id="showOnlyMyOrders"
                  name="showOnlyMyOrders"
                  type="checkbox"
                  defaultChecked={currentShowMyOrder}
                  onChange={(e) => changeShowMyOrder(e.target.checked)}
                />
                <label htmlFor="showOnlyMyOrders" className="cursor-pointer">
                  {translate('orderlist.showonlymyorders')}
                </label>
              </div>
            )}
          {(isReseller || isResellerDetail) && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-8">
              <div className="relative">
                <label className="sr-only" htmlFor="searchQuery">
                  {translate('orderlist.search_by_ordernumber')}
                </label>
                <span className="icon icon-outlined absolute -translate-y-1/2 top-1/2 left-4 text-input-secondary">
                  search
                </span>
                <input
                  id="searchQuery"
                  className="form__input w-full max-w-[250px] md:max-w-[unset] md:w-fit bg-input-primary pl-10"
                  placeholder={translate('orderlist.search_by_ordernumber')}
                  defaultValue={searchQuery}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    const shouldHandleSearch = value.length >= 3;
                    setSearchQuery(shouldHandleSearch ? value : null);
                    if (shouldHandleSearch) {
                      changePageIndex(1);
                    }
                  }}
                />
              </div>
              {statuses && (
                <div>
                  <label className="sr-only" htmlFor="statusFilter">
                    {translate('orderlist.search_by_ordernumber')}
                  </label>
                  <select
                    id="statusFilter"
                    className="w-full max-w-[250px] md:max-w-[unset] md:w-fit form__select"
                    onChange={(e) => {
                      setStatusFilter(statuses[e.currentTarget.value]);
                      changePageIndex(1);
                    }}
                    defaultValue={statusFilter}
                  >
                    {Object.entries(statuses).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          {isLoading && <LoaderOverlay />}
          <OrderHistoryList
            onShowDetail={showDetail}
            onApproveOrderCallback={onApproveOrderCallback}
            isBusinessCustomer={isBusinessCustomer}
            hasApproverRole={hasApproverRole}
            isReseller={isReseller || isResellerDetail}
          />
        </div>
      )}

      {mode && mode === ViewMode.List && (
        <Pagination
          model={pager}
          onChange={changePageIndex}
          disabled={isLoading}
        />
      )}
    </Fragment>
  );
};

export default OrderListContainer;
